import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bell, ArrowLeft, Smartphone, Globe } from 'lucide-react';
import { useAuth } from '../App';
import BottomTabBar from './BottomTabBar';
import { Capacitor } from '@capacitor/core';
import pushNotificationService from '../services/pushNotifications';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * NotificationSettingsPage - Dedicated page for notification preferences
 *
 * IMPORTANT: Product copy and badges are intentional and must not be altered.
 * - Mobile Push Notifications: Available Now (native app only)
 * - Browser Notifications (Web): Available Now
 *
 * Per-type notification toggles are intentionally NOT exposed yet — the backend
 * does not enforce `notification_settings.push_*` flags at send time, so adding
 * UI toggles here would be cosmetic fakes. Re-introduce only when send-time
 * enforcement lands in `push.py`.
 */
const NotificationSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  
  // Detect if running as native mobile app
  const isNativeApp = Capacitor.isNativePlatform();
  const [mobilePushEnabled, setMobilePushEnabled] = useState(false);
  const [mobilePushLoading, setMobilePushLoading] = useState(false);

  // Self-test push (regular user — calls existing POST /api/push/test)
  const [testPushLoading, setTestPushLoading] = useState(false);
  const [testPushResult, setTestPushResult] = useState(null);

  const handleSendTestPushToMe = async () => {
    setTestPushLoading(true);
    setTestPushResult(null);
    try {
      const authToken = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
      const testRes = await axios.post(`${API}/api/push/test`, {}, { headers });
      const data = testRes.data || {};
      const nat = data.result?.native || {};
      const web = data.result?.web || {};
      setTestPushResult({
        success: !!data.success,
        totalSent: data.result?.total_sent ?? 0,
        nativeSent: nat.sent ?? 0,
        nativeNoTokens: !!nat.no_tokens,
        webSent: web.sent ?? 0,
        webNoSubs: !!web.no_subscriptions,
        fcmAvailable: data.fcm_available,
        webpushAvailable: data.webpush_available,
        error: null,
      });
      console.log('PUSH_DEBUG: /api/push/test →', data);
      if (data.success) {
        toast.success(`Test push sent (${data.result?.total_sent ?? 0})`);
      } else if (nat.no_tokens && web.no_subscriptions) {
        toast.error('No active device tokens or web subscriptions yet');
      } else {
        toast.error('Test push could not be delivered');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Unknown error';
      setTestPushResult({ success: false, error: msg });
      toast.error(`Test push failed: ${msg}`);
    } finally {
      setTestPushLoading(false);
    }
  };

  // Determine if user is in vendor mode
  const isVendor = user?.roles?.includes('vendor') && localStorage.getItem('activeRole') === 'vendor';

  // Detect iOS device (Safari on iOS supports push, other iOS browsers don't)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOSNonSafari = isIOS && !isSafari;

  // Check browser push notification support and permission
  useEffect(() => {
    const checkPushSupport = () => {
      const supported = 'Notification' in window && 'serviceWorker' in navigator;
      setPushSupported(supported);
      if (supported) {
        setPushPermission(Notification.permission);
        setPushEnabled(Notification.permission === 'granted');
      }
    };
    checkPushSupport();
    
    // Check native push status if in mobile app
    if (isNativeApp) {
      // First check stored preference (this is the user's choice)
      const storedPref = pushNotificationService.getStoredPreference();
      setMobilePushEnabled(storedPref);
      
      // Then verify actual permission status if preference is ON
      if (storedPref && pushNotificationService.isSupported && pushNotificationService.isSupported()) {
        pushNotificationService.checkPermissions().then(status => {
          if (status && status.receive !== 'granted') {
            // Permission was revoked in device settings - update preference
            setMobilePushEnabled(false);
            pushNotificationService.setStoredPreference(false);
          }
        }).catch((e) => {
          console.log('PUSH_DEBUG: checkPermissions failed in NotificationSettings:', e);
        });
      }
    }
  }, [isNativeApp]);

  // Fetch current settings on mount (for future email preferences)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const endpoint = isVendor 
          ? `${API}/api/vendors/me/notification-settings`
          : `${API}/api/users/me/notification-settings`;
        
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        
        if (response.data) {
          setSettings(prev => ({
            ...prev,
            ...response.data
          }));
        }
      } catch (error) {
        console.log('Using default notification settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [isVendor]);

  const handleEnableBrowserNotifications = async () => {
    if (!pushSupported) return;
    
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission === 'granted') {
        setPushEnabled(true);
        toast.success('Browser notifications enabled');
      } else if (permission === 'denied') {
        toast.error('Please allow notifications in your browser settings');
      }
    } catch (error) {
      toast.error('Could not enable notifications');
    }
  };

  // Handle toggling mobile push notifications (native app only)
  const handleToggleMobilePush = async (newValue) => {
    if (!isNativeApp) return;
    
    // If turning OFF - just update preference, no permission request needed
    if (!newValue) {
      pushNotificationService.disable();
      setMobilePushEnabled(false);
      toast.info('Push notifications disabled');
      return;
    }
    
    // Turning ON - check if push service is available
    if (!pushNotificationService.isSupported || !pushNotificationService.isSupported()) {
      toast.error('Push notifications not available in this environment');
      return;
    }
    
    setMobilePushLoading(true);
    try {
      // Request permissions and enable push
      const result = await pushNotificationService.enable();
      
      if (result.success) {
        setMobilePushEnabled(true);
        toast.success('Push notifications enabled!');
      } else if (result.error === 'Permission denied') {
        toast.error('Please allow notifications in your device Settings app');
      } else {
        toast.error(result.error || 'Could not enable notifications');
      }
    } catch (error) {
      console.error('PUSH_DEBUG: Failed to enable push:', error);
      toast.error('Could not enable notifications');
    } finally {
      setMobilePushLoading(false);
    }
  };

  // Check if mobile push is actually available (not just native platform)
  const isMobilePushAvailable = isNativeApp && 
    pushNotificationService.isSupported && 
    pushNotificationService.isSupported();

  const goBack = () => {
    if (isVendor) {
      navigate('/vendor/dashboard?tab=account');
    } else {
      navigate('/customer-dashboard?tab=profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-lg mx-auto">
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              Loading preferences...
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 md:pb-0" data-testid="notification-settings-page">
      {/* Celey Header - matches other app pages */}
      <div className="bg-white border-b sticky top-0 z-10" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <img 
            src="/celeyapp-icon-green.png" 
            alt="Celey" 
            className="h-8 w-8"
          />
          <div>
            <span className="text-xl font-bold text-gray-900 leading-tight">Celey</span>
            <p className="text-xs text-gray-500">A place to be found</p>
          </div>
        </div>
      </div>
      
      {/* Back button - matches Account Settings page */}
      <div className="bg-gray-50 py-3">
        <div className="max-w-lg mx-auto px-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={goBack}
            className="text-gray-600"
            data-testid="back-button"
          >
            ← Back to Account
          </Button>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">

        {/* ============================================ */}
        {/* MOBILE PUSH NOTIFICATIONS — native app only */}
        {/* ============================================ */}
        {isNativeApp && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Smartphone className="h-5 w-5 text-purple-600" />
                Mobile Push Notifications
              </CardTitle>
              <Badge
                variant="outline"
                className="text-xs whitespace-nowrap px-3 py-0.5 flex-shrink-0 bg-emerald-50 text-emerald-700 border-emerald-200"
              >
                Available Now
              </Badge>
            </div>
            <CardDescription className="text-sm text-gray-600 mt-2">
              {isMobilePushAvailable
                ? 'Receive instant push notifications on your device.'
                : 'Push notifications not available in simulator. Test on a real device.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
              {isMobilePushAvailable ? (
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Push Notifications</p>
                    <p className="text-xs text-gray-500">
                      {mobilePushEnabled 
                        ? 'Enabled on this device' 
                        : 'Click to enable notifications'}
                    </p>
                  </div>
                  <Switch
                    checked={mobilePushEnabled}
                    onCheckedChange={handleToggleMobilePush}
                    disabled={mobilePushLoading}
                    data-testid="switch-mobile-push"
                  />
                </div>
              ) : (
                <div className="py-3 text-center">
                  <p className="text-sm text-amber-600">
                    Push notifications not available in simulator
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Test on a physical device to enable push notifications.
                  </p>
                </div>
              )}

              {/* Self-test push — minimal, ADMIN-ONLY. Calls existing POST /api/push/test.
                  Feb 2026: gated to admins to avoid exposing internal diagnostic
                  UI to end users in production / TestFlight builds. */}
              {isMobilePushAvailable && (user?.is_admin || user?.roles?.includes('admin')) && (
                <div className="pt-3 mt-3 border-t border-gray-100">
                  <Button
                    onClick={handleSendTestPushToMe}
                    disabled={testPushLoading || !mobilePushEnabled}
                    variant="outline"
                    size="sm"
                    className="w-full"
                    data-testid="send-test-push-to-me"
                  >
                    {testPushLoading ? 'Sending…' : 'Send test push to me'}
                  </Button>
                  {testPushResult && (
                    <div
                      className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700"
                      data-testid="send-test-push-result"
                    >
                      {testPushResult.error ? (
                        <p className="text-red-600">Error: {testPushResult.error}</p>
                      ) : (
                        <>
                          <p>
                            <span className="font-medium">Result:</span>{' '}
                            {testPushResult.success
                              ? `Sent ${testPushResult.totalSent}`
                              : 'Not sent'}
                          </p>
                          <p className="mt-1">
                            Native: {testPushResult.nativeSent} sent
                            {testPushResult.nativeNoTokens ? ' · no device token yet' : ''}
                          </p>
                          <p>
                            Web: {testPushResult.webSent} sent
                            {testPushResult.webNoSubs ? ' · no web subscription' : ''}
                          </p>
                          {(testPushResult.fcmAvailable !== undefined ||
                            testPushResult.webpushAvailable !== undefined) && (
                            <p className="mt-1 text-gray-500">
                              FCM: {testPushResult.fcmAvailable ? 'on' : 'off'} · Web Push:{' '}
                              {testPushResult.webpushAvailable ? 'on' : 'off'}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
          </CardContent>
        </Card>
        )}

        {/* ============================================ */}
        {/* BROWSER NOTIFICATIONS (WEB) — web browsers only */}
        {/* ============================================ */}
        {!isNativeApp && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Globe className="h-5 w-5 text-emerald-600" />
                Browser Notifications (Web)
              </CardTitle>
              <Badge 
                variant="outline" 
                className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 whitespace-nowrap px-3 py-0.5 flex-shrink-0"
              >
                Available Now
              </Badge>
            </div>
            <CardDescription className="text-sm text-gray-600 mt-2">
              Receive instant alerts in this browser. Notifications are browser- and device-specific.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pushSupported ? (
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">Browser Notifications</p>
                  <p className="text-xs text-gray-500">
                    {pushPermission === 'granted' 
                      ? 'Enabled for this browser' 
                      : pushPermission === 'denied'
                        ? 'Blocked — update in browser settings'
                        : 'Click to enable'}
                  </p>
                </div>
                <Switch
                  checked={pushEnabled}
                  onCheckedChange={handleEnableBrowserNotifications}
                  disabled={pushPermission === 'denied'}
                  data-testid="switch-browser-notifications"
                />
              </div>
            ) : (
              <div className="py-3">
                <p className="text-sm text-gray-500">
                  Browser notifications aren't supported in this browser.
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  Tip: Open Celey in Safari to enable browser notifications.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200 mb-20">
          <CardContent className="py-4">
            <p className="text-sm font-medium text-blue-800 mb-1">💡 Stay Connected</p>
            <p className="text-xs text-blue-700">
              {isNativeApp
                ? (isVendor
                    ? "Enable push notifications to respond quickly to new requests. Fast responses lead to more bookings!"
                    : "Enable push notifications so you don't miss important updates about your bookings.")
                : (isVendor
                    ? "Enable browser notifications to respond quickly to new requests. Fast responses lead to more bookings!"
                    : "Enable browser notifications so you don't miss important updates about your bookings.")
              }
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Bottom Tab Bar */}
      <BottomTabBar 
        variant={isVendor ? 'vendor' : 'customer'} 
        activeTab="account"
      />
    </div>
  );
};

export default NotificationSettingsPage;
